import { createClient } from '@/prismicio';
import { BlogDocument } from '../../../prismicio-types';
import { filter } from '@prismicio/client';
import { blogCardQuery } from '@/components/blogHome/constants';
import BlogCard from './BlogCard';
import { BlogCardPagination } from './BlogCardPagination';
import { FeaturedBlogColumn } from './FeaturedBlogColumn';
import { PopularBlogGrid } from './PopularBlogGrid';
import PopularLoadMoreServer from './PopularLoadMoreServer';

// Data fetching (server side)
export const fetchBlogPosts = async (
  page: number,
  category?: string,
  authorId?: string,
) => {
  try {
    const client = createClient();
    const categoryId = category
      ? (await client.getByUID('blog_category', category)).id
      : null;
    const mainBlog = await client.getFirst<BlogDocument>({
      filters: [filter.at('document.type', 'blog')],
      orderings: [
        { field: 'document.last_publication_date', direction: 'desc' },
      ],
      graphQuery: blogCardQuery,
    });
    const response = await client.get<BlogDocument>({
      page,
      filters: [
        filter.at('document.type', 'blog'),
        ...(mainBlog && !authorId
          ? [filter.not('document.id', mainBlog.id)]
          : []),
        ...(categoryId
          ? [filter.at('my.blog.all_categories.category', categoryId)]
          : []),
        ...(authorId ? [filter.at('my.blog.author', authorId)] : []),
      ],
      orderings: [
        { field: 'document.last_publication_date', direction: 'desc' },
      ],
      graphQuery: blogCardQuery,
      pageSize: 6,
    });
    const featured = await client.get<BlogDocument>({
      page,
      filters: [
        filter.at('document.type', 'blog'),
        ...(mainBlog && !authorId
          ? [filter.not('document.id', mainBlog.id)]
          : []),
        ...(authorId ? [filter.at('my.blog.author', authorId)] : []),
        filter.at('my.blog.featured', true),
      ],
      orderings: [
        { field: 'document.last_publication_date', direction: 'desc' },
      ],
      graphQuery: blogCardQuery,
      pageSize: 3,
    });
    const popular = await client.get<BlogDocument>({
      filters: [
        filter.at('document.type', 'blog'),
        ...(categoryId
          ? [filter.at('my.blog.all_categories.category', categoryId)]
          : []),
      ],
      orderings: [
        { field: 'document.last_publication_date', direction: 'desc' },
      ],
      pageSize: 5,
      graphQuery: blogCardQuery,
    });
    return {
      blogs: response.results,
      mainBlog: page === 1 ? mainBlog : null,
      hasNextPage: !!response.next_page,
      hasPrevPage: !!response.prev_page,
      totalPages: response.total_pages,
      popular: popular.results,
      featured: featured.results,
    };
  } catch (err) {
    console.error('Error fetching data:', err);
    return {
      blogs: [] as BlogDocument[],
      mainBlog: null,
      hasNextPage: false,
      hasPrevPage: false,
      totalPages: 0,
      popular: null,
      featured: null,
    };
  }
};

interface Props {
  page: number;
  selectedCategory?: string;
}

// Summary component with fixed desktop pixel specs: main 716px, gap 60px, featured column 316px, side padding 174px (overall 1440px width region)
export const Summary = async ({ selectedCategory, page }: Readonly<Props>) => {
  const {
    blogs,
    mainBlog,
    hasPrevPage,
    hasNextPage,
    totalPages,
    popular,
    featured,
  } = await fetchBlogPosts(page, selectedCategory);

  const desktopView = () => (
    <>
      <div className="w-[1440px] mx-auto pr-[174px] pl-[45px]">
        {/* Breadcrumbs alignment anchor - replace with actual Breadcrumb component if needed */}
        <div id="blog-alignment-anchor" className="mb-8" />
        <div className="flex flex-row gap-[60px] mb-16 items-stretch relative">
          {mainBlog && (
            <div
              className="flex flex-col h-full"
              style={{
                width: '890px',
                height: '890px',
                minHeight: featured?.length ? '100%' : undefined,
                marginLeft: '-45px',
              }}
            >
              <BlogCard
                {...mainBlog.data}
                cardType="new-main"
                uid={mainBlog.uid}
                date={mainBlog.last_publication_date}
                containerClass="w-full h-full"
              />
            </div>
          )}
          {featured && featured.length > 0 && (
            <div className="flex flex-col h-full" style={{ width: '316px' }}>
              <FeaturedBlogColumn>
                <div className="flex flex-col gap-4 h-full">
                  {featured.map(
                    ({ data, uid, last_publication_date }, index) => (
                      <BlogCard
                        key={`blog_featured_${index}`}
                        {...data}
                        cardType="new-featured"
                        uid={uid}
                        containerClass="grow-0 w-full"
                        date={last_publication_date}
                      />
                    ),
                  )}
                </div>
              </FeaturedBlogColumn>
            </div>
          )}
        </div>
      </div>
      {popular && popular.length > 0 && (
        <div
          className="w-full bg-[#0E1110] py-10 mt-16 overflow-hidden"
          style={{
            position: 'relative',
            left: '50%',
            right: '50%',
            marginLeft: '-50vw',
            marginRight: '-50vw',
            width: '100vw',
            boxSizing: 'border-box',
          }}
        >
          <div className="w-[1440px] mx-auto pr-[174px] pl-[45px] relative">
            <div
              className="relative"
              style={{ marginLeft: '56px', width: '1266px' }}
            >
              <PopularBlogGrid>
                {popular.map(({ data, uid, last_publication_date }, index) => (
                  <BlogCard
                    key={`blog_popular_${index}`}
                    {...data}
                    cardType="new-simple"
                    uid={uid}
                    date={last_publication_date}
                  />
                ))}
              </PopularBlogGrid>
              <div className="w-full mt-6 flex justify-center">
                <PopularLoadMoreServer
                  startPage={1}
                  category={selectedCategory ?? null}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const mobileView = () => {
    const allBlogs = mainBlog ? [mainBlog, ...blogs] : blogs;
    return (
      <BlogCardPagination
        selectedCategory={selectedCategory}
        hasPrevPage={hasPrevPage}
        hasNextPage={hasNextPage}
        currentPage={page}
        totalPages={totalPages}
      >
        {allBlogs.map(({ data, uid, last_publication_date }, index) => (
          <BlogCard
            key={`blog_mobile_${index}`}
            {...data}
            uid={uid}
            containerClass="grow-0"
            date={last_publication_date}
            cardType="new-simple"
          />
        ))}
      </BlogCardPagination>
    );
  };

  return (
    <>
      <div className="hidden md:block w-full">{desktopView()}</div>
      <div className="block md:hidden w-full relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0E1110] to-[#0F423A] z-0 text-white" />
        <div className="relative z-10 py-6 px-6">{mobileView()}</div>
      </div>
    </>
  );
};
